import { useInfiniteQuery } from "@tanstack/react-query";
import { useUserService } from "contexts/ServiceProvider";
import { UserListSortColumns } from "services/UserService";
import {
  QueryParams,
  getNextPage,
  transformProfilePictureResponse,
} from "services/apiHelpers";
import {
  FarmUserClaimDto,
  UserResponse,
  UserRoleDto,
  UserStatusDto,
} from "api";

export type UserListItem = {
  id: string;
  name: string;
  profilePicture?: ProfilePicture;
  email: string;
  phoneNumber?: string;
  farms?: FarmUserClaimDto[];
  roles: UserRoleDto[];
  status: UserStatusDto;
};

export const GET_USERS_QUERY_KEY = "get-users";

export const transformUserResponse = (user: UserResponse): UserListItem => {
  return {
    id: user.id,
    name: `${user.firstName} ${user.lastName}`,
    profilePicture: transformProfilePictureResponse(user.profilePicture),
    email: user.email,
    phoneNumber: user.phoneNumber,
    farms: user.farms,
    roles: user.role,
    status: user.status,
  };
};

const useUsersList = (
  queryParams?: Omit<QueryParams<UserListSortColumns>, "page"> & {
    farmId?: string;
  }
) => {
  const userService = useUserService();

  return useInfiniteQuery({
    queryKey: [GET_USERS_QUERY_KEY, queryParams],
    queryFn: ({ pageParam = 0 }) =>
      userService
        .getUsers({
          ...queryParams,
          page: pageParam,
        })
        .then(({ data, meta }) => ({
          users: data.map(transformUserResponse),
          meta,
        })),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => getNextPage(lastPage),
  });
};

export default useUsersList;
